/**
 * ResizableTableColumnsReset
 * 
 * Manages the reset functionality for resizable table columns.
 * - Shows/hides reset link based on localStorage data
 * - Monitors storage changes to update visibility dynamically
 * - Clears stored widths and restores defaults without page refresh
 */
class ResizableTableColumnsReset {
  /**
   * @param {HTMLElement} table - The table element with resizable columns
   * @param {HTMLElement} resetLink - The reset link element to show/hide
   * @param {string} storageKey - The localStorage key for this table (e.g., 'rtc/links-results-table')
   * @param {Object} options - ResizableTableColumns initialization options
   */
  constructor(table, resetLink, storageKey, options = {}) {
    this.table = table;
    this.resetLink = resetLink;
    this.storageKey = storageKey;
    this.options = options;
    this.rtcInstance = null;

    this.init();
    this.wrapStoreMethod();
  }

  /**
   * Initialize the reset functionality
   */
  init() {
    this.updateResetLinkVisibility();
    this.resetLink.addEventListener('click', (e) => {
      e.preventDefault();
      this.reset();
    });
  }

  /**
   * Wrap store.set to monitor changes to this table's storage key
   */
  wrapStoreMethod() {
    if (typeof store === 'undefined' || !store.set) return;

    if (!store._originalSet) {
      store._originalSet = store.set;
    }

    const self = this;
    const originalSet = store._originalSet;

    store.set = function(key, value) {
      const result = originalSet.call(this, key, value);
      
      if (key === self.storageKey) {
        self.updateResetLinkVisibility();
      }
      
      return result;
    };
  }

  /**
   * Show/hide reset link based on stored data
   */
  updateResetLinkVisibility() {
    const hasStoredData = store.get(this.storageKey);
    this.resetLink.style.display = hasStoredData ? 'inline-block' : 'none';
  }

  /**
   * Reset column widths to defaults
   */
  reset() {
    this.clearStoredWidths();
    this.clearDOMWidths();
    this.syncResizeHandles();
  }

  /**
   * Clear stored widths from localStorage
   */
  clearStoredWidths() {
    store.remove(this.storageKey);
    this.resetLink.style.display = 'none';
  }

  /**
   * Remove inline width styles from table cells
   */
  clearDOMWidths() {
    // Clear header widths
    this.table.querySelectorAll('th[data-rtc-resizable]').forEach(header => {
      header.style.width = '';
    });

    // Clear body cell widths
    this.table.querySelectorAll('tbody tr').forEach(row => {
      row.querySelectorAll('td').forEach(cell => {
        cell.style.width = '';
      });
    });
  }

  /**
   * Sync resize handles with updated column widths
   */
  syncResizeHandles() {
    if (this.rtcInstance && typeof this.rtcInstance.syncHandleWidths === 'function') {
      this.rtcInstance.syncHandleWidths();
    }
  }

  /**
   * Set the ResizableTableColumns instance
   * This can be called after creating the RTC instance if needed
   */
  setRtcInstance(instance) {
    this.rtcInstance = instance;
  }

  /**
   * Cleanup - restore original store.set method
   */
  destroy() {
    // Restore original store.set if we wrapped it
    if (typeof store !== 'undefined' && store._originalSet) {
      store.set = store._originalSet;
      delete store._originalSet;
    }
  }
}

export default ResizableTableColumnsReset;
